import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Match } from '../services/api';

interface PlayerStatInput {
  two_pm: number | '';
  two_pa: number | '';
  three_pm: number | '';
  three_pa: number | '';
  ftm: number | '';
  fta: number | '';
  oreb: number | '';
  dreb: number | '';
  ast: number | '';
  to: number | '';
  st: number | '';
  bs: number | '';
  pf: number | '';
}

interface PlayerEntry {
  player_id: string;
  name: string;
  jersey: number;
  stats: PlayerStatInput;
}

interface MatchEntryDetail {
  match: {
    id: string;
    match_no: number;
    year: number;
    group: string;
    team1: { id: string; name: string; logo: string | null } | null;
    team2: { id: string; name: string; logo: string | null } | null;
  };
  team1_players: PlayerEntry[];
  team2_players: PlayerEntry[];
}

const AVAILABLE_FIELDS = [
  { key: 'two_pm', label: '2PM (二分進球)' },
  { key: 'two_pa', label: '2PA (二分出手)' },
  { key: 'three_pm', label: '3PM (三分進球)' },
  { key: 'three_pa', label: '3PA (三分出手)' },
  { key: 'ftm', label: 'FTM (罰球進球)' },
  { key: 'fta', label: 'FTA (罰球出手)' },
  { key: 'oreb', label: 'OREB (進攻籃板)' },
  { key: 'dreb', label: 'DREB (防守籃板)' },
  { key: 'ast', label: 'AST (助攻)' },
  { key: 'to', label: 'TO (失誤)' },
  { key: 'st', label: 'ST (抄截)' },
  { key: 'bs', label: 'BS (阻攻)' },
  { key: 'pf', label: 'PF (犯規)' },
];

const DEFAULT_VISIBLE_FIELDS = [
  'two_pm', 'two_pa',
  'three_pm', 'three_pa',
  'ftm', 'fta',
  'oreb', 'dreb',
  'ast', 'to', 'st', 'bs',
  'pf'
];

const getShortLabel = (key: string) => {
  const mapping: { [key: string]: string } = {
    two_pm: '2PM',
    two_pa: '2PA',
    three_pm: '3PM',
    three_pa: '3PA',
    ftm: 'FTM',
    fta: 'FTA',
    oreb: 'ORB',
    dreb: 'DRB',
    ast: 'AST',
    to: 'TO',
    st: 'STL',
    bs: 'BLK',
    pf: 'PF',
  };
  return mapping[key] || key.toUpperCase();
};

const AdminStats: React.FC = () => {
  const getAdminUrl = () => {
    return window.location.port === '3000' ? `http://${window.location.hostname}:8000/admin/` : '/admin/';
  };

  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | ''>('');
  const [filteredMatches, setFilteredMatches] = useState<Match[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<string>('');

  const [matchDetail, setMatchDetail] = useState<MatchEntryDetail | null>(null);
  const [stats, setStats] = useState<{ [playerId: string]: PlayerStatInput }>({});
  const [initialStats, setInitialStats] = useState<{ [playerId: string]: PlayerStatInput }>({});

  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);

  const checkUserStatus = async (retryCount = 0) => {
    try {
      const status = await apiService.getUserStatus();
      console.log(`[checkUserStatus] Attempt ${retryCount}:`, status, 'Hostname:', window.location.hostname);
      if (status.is_authenticated) {
        setCurrentUser(status.username);
        if (status.is_staff) {
          setUnauthorized(false);
          return;
        } else {
          setUnauthorized(true);
        }
      } else {
        setCurrentUser(null);
        setUnauthorized(true);
      }
    } catch (err) {
      console.error('Failed to check user status:', err);
    }

    // Retry checking if not authenticated yet (handles browser cookie storage write latency)
    if (retryCount < 4) {
      setTimeout(() => {
        checkUserStatus(retryCount + 1);
      }, 300);
    }
  };

  // Check user status on mount and prevent indexing
  useEffect(() => {
    checkUserStatus();

    // Prevent search engine crawlers from indexing this page
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Listen to message events from the login popup success redirect view
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'LOGIN_SUCCESS') {
        checkUserStatus();
        setMessage({ text: '登入成功！已自動偵測權限並重新載入數據。', type: 'success' });

        // Re-trigger fetch of stats entry if match is selected
        if (selectedMatchId) {
          const current = selectedMatchId;
          setSelectedMatchId('');
          setTimeout(() => setSelectedMatchId(current), 50);
        } else {
          // If no match selected, fetch matches list again just in case
          apiService.getMatches().then(matches => {
            setAllMatches(matches);
            const uniqueYears = Array.from(new Set(matches.map(m => m.year))).sort((a, b) => b - a);
            setYears(uniqueYears);
          }).catch(console.error);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [selectedMatchId]);

  const handleLoginClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const backendBase = window.location.port === '3000' ? `http://${window.location.hostname}:8000` : '';
    const loginUrl = `${backendBase}/api/admin-login/?next=/api/admin-login-success/`;

    const width = 800;
    const height = 650;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      loginUrl,
      'DjangoAdminLogin',
      `width=${width},height=${height},left=${left},top=${top},status=yes,resizable=yes,scrollbars=yes`
    );

    if (popup) {
      popup.focus();
    }
  };

  // Load matches to populate dropdowns
  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const matches = await apiService.getMatches();
        setAllMatches(matches);

        // Extract unique years
        const uniqueYears = Array.from(new Set(matches.map(m => m.year))).sort((a, b) => b - a);
        setYears(uniqueYears);

        if (uniqueYears.length > 0) {
          setSelectedYear(uniqueYears[0]);
        }
      } catch (err) {
        console.error('Failed to load matches:', err);
        setMessage({ text: '載入比賽列表失敗，請重新整理頁面。', type: 'error' });
      } finally {
        setLoadingMatches(false);
      }
    };

    fetchMatches();
  }, []);

  // Filter matches when year changes
  useEffect(() => {
    if (selectedYear !== '') {
      const filtered = allMatches
        .filter(m => m.year === selectedYear)
        .sort((a, b) => a.match_no - b.match_no);
      setFilteredMatches(filtered);
      setSelectedMatchId('');
      setMatchDetail(null);
    } else {
      setFilteredMatches([]);
      setSelectedMatchId('');
      setMatchDetail(null);
    }
  }, [selectedYear, allMatches]);

  // Load match players and existing stats when match selection changes
  useEffect(() => {
    if (!selectedMatchId) {
      setMatchDetail(null);
      return;
    }

    const fetchMatchStatsEntry = async () => {
      setLoadingDetail(true);
      setMessage(null);
      setUnauthorized(false);
      try {
        const detail = await apiService.getMatchStatsEntry(selectedMatchId);
        setMatchDetail(detail);

        // Populate stats editor state
        const playerStatsData: { [playerId: string]: PlayerStatInput } = {};

        const processPlayer = (p: PlayerEntry) => {
          playerStatsData[p.player_id] = {
            two_pm: p.stats.two_pm ?? '',
            two_pa: p.stats.two_pa ?? '',
            three_pm: p.stats.three_pm ?? '',
            three_pa: p.stats.three_pa ?? '',
            ftm: p.stats.ftm ?? '',
            fta: p.stats.fta ?? '',
            oreb: p.stats.oreb ?? '',
            dreb: p.stats.dreb ?? '',
            ast: p.stats.ast ?? '',
            to: p.stats.to ?? '',
            st: p.stats.st ?? '',
            bs: p.stats.bs ?? '',
            pf: p.stats.pf ?? '',
          };
        };

        detail.team1_players.forEach(processPlayer);
        detail.team2_players.forEach(processPlayer);

        setStats(playerStatsData);
        setInitialStats(JSON.parse(JSON.stringify(playerStatsData)));
      } catch (err: any) {
        console.error('Error fetching match stats entry:', err);
        if (err.message && err.message.includes('403')) {
          setUnauthorized(true);
          setCurrentUser(null);
        } else {
          setMessage({ text: '載入比賽數據失敗，請確認登入狀態。', type: 'error' });
        }
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchMatchStatsEntry();
  }, [selectedMatchId]);

  const handleStatChange = (playerId: string, field: keyof PlayerStatInput, value: string) => {
    setStats(prev => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: value === '' ? '' : parseInt(value) || 0
      }
    }));
  };

  const calculatePts = (pStat?: PlayerStatInput) => {
    if (!pStat) return 0;
    const two_pm = typeof pStat.two_pm === 'number' ? pStat.two_pm : 0;
    const three_pm = typeof pStat.three_pm === 'number' ? pStat.three_pm : 0;
    const ftm = typeof pStat.ftm === 'number' ? pStat.ftm : 0;
    return two_pm * 2 + three_pm * 3 + ftm;
  };

  const calculateReb = (pStat?: PlayerStatInput) => {
    if (!pStat) return 0;
    const oreb = typeof pStat.oreb === 'number' ? pStat.oreb : 0;
    const dreb = typeof pStat.dreb === 'number' ? pStat.dreb : 0;
    return oreb + dreb;
  };

  const getChangedRowsCount = () => {
    let count = 0;
    Object.keys(stats).forEach(playerId => {
      const current = stats[playerId];
      const initial = initialStats[playerId];
      if (!initial) return;

      const hasChanged = Object.keys(current).some(key => {
        const k = key as keyof PlayerStatInput;
        const curVal = current[k] === '' ? null : current[k];
        const initVal = initial[k] === '' ? null : initial[k];
        return curVal !== initVal;
      });

      if (hasChanged) {
        count++;
      }
    });
    return count;
  };

  const handleSave = async () => {
    if (!selectedMatchId) return;

    const changedCount = getChangedRowsCount();
    if (changedCount === 0) {
      alert('沒有偵測到任何數據修改，無需儲存。');
      return;
    }

    if (!window.confirm(`偵測到共 ${changedCount} 位球員的數據有修改，確定要儲存嗎？`)) {
      return;
    }

    setSaving(true);
    setMessage(null);
    setUnauthorized(false);

    try {
      // Transform stats state back to list of objects for API
      const statsPayload = Object.entries(stats).map(([playerId, playerStats]) => ({
        player_id: playerId,
        ...playerStats
      }));

      await apiService.saveMatchStatsEntry(selectedMatchId, statsPayload);
      setMessage({ text: '數據儲存成功！已自動更新相關統計、榜單與快取。', type: 'success' });
      setInitialStats(JSON.parse(JSON.stringify(stats)));

      // Scroll to top to see success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Error saving stats:', err);
      if (err.message && (err.message.includes('403') || err.message.includes('Forbidden') || err.message.includes('privilege'))) {
        setUnauthorized(true);
        setCurrentUser(null);
        setMessage({ text: '儲存失敗：您沒有管理員權限，請先登入 Django Admin。', type: 'error' });
      } else {
        setMessage({ text: `儲存失敗：${err.message || '伺服器錯誤'}`, type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('確定要放棄目前修改並重新載入嗎？')) {
      // Re-trigger the selection effect
      const current = selectedMatchId;
      setSelectedMatchId('');
      setTimeout(() => setSelectedMatchId(current), 10);
    }
  };

  // Render stats table for a team
  const renderStatsTable = (players: PlayerEntry[], teamName: string, logo: string | null) => {
    if (players.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '20px', color: '#718096', fontStyle: 'italic' }}>
          本球隊目前沒有球員資料
        </div>
      );
    }

    return (
      <div className="modern-table-wrapper" style={{ margin: '16px 0 32px 0', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
        <table className="modern-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center', fontSize: '11px', padding: '6px 2px', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>號碼</th>
              <th style={{ textAlign: 'left', width: '75px', fontSize: '11px', padding: '6px 4px', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>球員姓名</th>
              {AVAILABLE_FIELDS.map(f => (
                <th key={f.key} style={{ width: '42px', textAlign: 'center', fontSize: '11px', padding: '6px 2px', backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {getShortLabel(f.key)}
                </th>
              ))}
              <th className="highlight-stat" style={{
                width: '42px', textAlign: 'center', backgroundColor: '#f0fdf4', fontSize: '11px', padding: '6px 2px',
                position: 'sticky', right: 42, zIndex: 2,
                borderBottom: '2px solid #e2e8f0',
                boxShadow: '-2px 0 4px -1px rgba(0,0,0,0.08)'
              }}>得分</th>
              <th className="highlight-stat" style={{
                width: '42px', textAlign: 'center', backgroundColor: '#eff6ff', fontSize: '11px', padding: '6px 2px',
                position: 'sticky', right: 0, zIndex: 2,
                borderBottom: '2px solid #e2e8f0'
              }}>籃板</th>
            </tr>
          </thead>
          <tbody>
            {players.map(p => {
              const playerStats = stats[p.player_id] || {
                two_pm: '', two_pa: '', three_pm: '', three_pa: '',
                ftm: '', fta: '', oreb: '', dreb: '', ast: '',
                to: '', st: '', bs: '', pf: ''
              };

              const pts = calculatePts(playerStats);
              const reb = calculateReb(playerStats);

              return (
                <tr key={p.player_id}>
                  <td style={{ fontWeight: 'bold', textAlign: 'center', fontSize: '12px', padding: '4px 2px', borderBottom: '1px solid #edf2f7' }}>
                    #{p.jersey !== null ? p.jersey : '-'}
                  </td>
                  <td style={{ textAlign: 'left', fontWeight: '500', width: '75px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '12px', padding: '4px 4px', borderBottom: '1px solid #edf2f7' }} title={p.name}>
                    {p.name}
                  </td>
                  {AVAILABLE_FIELDS.map(f => {
                    const fieldName = f.key as keyof PlayerStatInput;
                    return (
                      <td key={f.key} style={{ padding: '3px 1px', borderBottom: '1px solid #edf2f7' }}>
                        <input
                          type="number"
                          min="0"
                          value={playerStats[fieldName]}
                          onChange={(e) => handleStatChange(p.player_id, fieldName, e.target.value)}
                          placeholder="0"
                          style={{
                            width: '34px',
                            padding: '3px 0',
                            textAlign: 'center',
                            border: '1px solid #cbd5e1',
                            borderRadius: '4px',
                            fontSize: '13px',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#cbd5e1';
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#94a3b8';
                          }}
                          onMouseLeave={(e) => {
                            if (document.activeElement !== e.currentTarget) {
                              e.currentTarget.style.borderColor = '#cbd5e1';
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                  <td style={{
                    fontWeight: 'bold', color: '#16a34a', backgroundColor: '#f0fdf4', textAlign: 'center', fontSize: '13px',
                    position: 'sticky', right: 42, zIndex: 1,
                    padding: '4px 2px', borderBottom: '1px solid #edf2f7',
                    boxShadow: '-2px 0 4px -1px rgba(0,0,0,0.08)'
                  }}>
                    {pts}
                  </td>
                  <td style={{
                    fontWeight: 'bold', color: '#2563eb', backgroundColor: '#eff6ff', textAlign: 'center', fontSize: '13px',
                    position: 'sticky', right: 0, zIndex: 1,
                    padding: '4px 2px', borderBottom: '1px solid #edf2f7'
                  }}>
                    {reb}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="modern-container" style={{ paddingBottom: '60px', maxWidth: '100%', width: '100%', paddingLeft: '12px', paddingRight: '12px' }}>
      <div style={{ marginBottom: '24px', paddingTop: '20px' }}>
        <Link to="/" style={{ color: '#1976d2', textDecoration: 'none', fontWeight: '600' }}>
          ← 返回首頁
        </Link>
      </div>

      <div className="modern-page-card" style={{ background: '#fff', padding: '24px 12px' }}>
        {/* Title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '28px', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px' }}>
          <div>
            <h1 style={{ fontSize: '2.2rem', color: '#0f172a', fontWeight: '800', margin: '0 0 8px 0' }}>
              📊 賽事數據管理員輸入
            </h1>
            <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>
              輕鬆輸入並更新每場比賽的球員數據。修改後將即時重算得分、籃板，並刷新排行榜。
            </p>
          </div>
          {currentUser ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              backgroundColor: '#f1f5f9',
              borderRadius: '20px',
              border: '1px solid #e2e8f0',
              color: '#334155',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              <span>👤 歡迎，{currentUser}</span>
            </div>
          ) : (
            <a
              href={getAdminUrl()}
              onClick={handleLoginClick}
              className="video-tab-btn"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
            >
              🔑 登入 Django 後台
            </a>
          )}
        </div>

        {/* Message Banner */}
        {message && (
          <div style={{
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            fontWeight: '600',
            fontSize: '15px',
            backgroundColor: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: message.type === 'success' ? '1px solid #a7f3d0' : '1px solid #fca5a5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>{message.type === 'success' ? '✅' : '❌'}</span>
            <div>{message.text}</div>
          </div>
        )}

        {/* Unauthorized Block */}
        {unauthorized && (
          <div style={{
            padding: '24px',
            borderRadius: '12px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fde68a',
            color: '#92400e',
            marginBottom: '32px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.2rem', fontWeight: '700' }}>⚠️ 權限不足 / 未登入</h3>
            <p style={{ margin: '0 0 18px 0', fontSize: '15px', lineHeight: '1.6' }}>
              您目前沒有權限修改賽事數據。請先在 Django Admin 登入為<strong>管理員 (Staff)</strong> 帳號，再返回此頁面操作。
            </p>
            <a
              href={getAdminUrl()}
              onClick={handleLoginClick}
              style={{
                display: 'inline-block',
                padding: '10px 20px',
                backgroundColor: '#d97706',
                color: 'white',
                borderRadius: '8px',
                fontWeight: '600',
                textDecoration: 'none',
                boxShadow: '0 2px 4px rgba(217, 119, 6, 0.2)',
                cursor: 'pointer'
              }}
            >
              立即登入 Admin 後台 →
            </a>
          </div>
        )}

        {/* Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '20px',
          backgroundColor: '#f8fafc',
          padding: '20px',
          borderRadius: '12px',
          marginBottom: '28px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Year Selector */}
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: '#334155', marginBottom: '8px', fontSize: '14px' }}>
              選擇年度 / Season
            </label>
            {loadingMatches ? (
              <div style={{ color: '#64748b', fontSize: '14px', padding: '8px 0' }}>載入中...</div>
            ) : (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : '')}
                disabled={unauthorized}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '15px',
                  backgroundColor: unauthorized ? '#f1f5f9' : '#fff',
                  outline: 'none',
                  cursor: unauthorized ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">-- 選擇年度 --</option>
                {years.map(y => (
                  <option key={y} value={y}>{y} 年</option>
                ))}
              </select>
            )}
          </div>

          {/* Match Selector */}
          <div>
            <label style={{ display: 'block', fontWeight: '700', color: '#334155', marginBottom: '8px', fontSize: '14px' }}>
              選擇比賽場次 / Match
            </label>
            <select
              value={selectedMatchId}
              onChange={(e) => setSelectedMatchId(e.target.value)}
              disabled={unauthorized || !selectedYear || filteredMatches.length === 0}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '15px',
                backgroundColor: unauthorized || !selectedYear || filteredMatches.length === 0 ? '#f1f5f9' : '#fff',
                outline: 'none',
                cursor: unauthorized || !selectedYear || filteredMatches.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">-- 選擇場次 --</option>
              {filteredMatches.map(m => (
                <option key={m.id} value={m.id}>
                  場次 {m.match_no} | {m.group} | {m.team1.name} vs {m.team2.name} ({m.date})
                </option>
              ))}
            </select>
          </div>
        </div>



        {/* Stats Editor */}
        {loadingDetail ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <span style={{ fontSize: '24px' }}>⏳</span>
            <p style={{ color: '#64748b', marginTop: '12px', fontWeight: '500' }}>正在載入球員與現有數據...</p>
          </div>
        ) : matchDetail ? (
          <div>
            {/* Team 1 (Home) */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                {matchDetail.match.team1?.logo && (
                  <img
                    src={matchDetail.match.team1.logo}
                    alt={matchDetail.match.team1.name}
                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                  />
                )}
                <h2 style={{ fontSize: '1.4rem', color: '#1e293b', fontWeight: '700', margin: 0 }}>
                  🏠 主隊：{matchDetail.match.team1?.name || 'Unknown Team'}
                </h2>
              </div>
              {renderStatsTable(matchDetail.team1_players, matchDetail.match.team1?.name || '', matchDetail.match.team1?.logo || null)}
            </div>

            {/* Team 2 (Away) */}
            <div style={{ marginBottom: '48px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                {matchDetail.match.team2?.logo && (
                  <img
                    src={matchDetail.match.team2.logo}
                    alt={matchDetail.match.team2.name}
                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                  />
                )}
                <h2 style={{ fontSize: '1.4rem', color: '#1e293b', fontWeight: '700', margin: 0 }}>
                  ✈️ 客隊：{matchDetail.match.team2?.name || 'Unknown Team'}
                </h2>
              </div>
              {renderStatsTable(matchDetail.team2_players, matchDetail.match.team2?.name || '', matchDetail.match.team2?.logo || null)}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              borderTop: '2px solid #f1f5f9',
              paddingTop: '24px',
              justifyContent: 'flex-end',
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'white',
              paddingBottom: '16px',
              boxShadow: '0 -10px 15px -3px rgba(255, 255, 255, 0.9)'
            }}>
              <button
                onClick={handleReset}
                disabled={saving}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#e2e8f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
              >
                🔄 重置修改
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '12px 32px',
                  backgroundColor: saving ? '#93c5fd' : '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s, transform 0.1s',
                  boxShadow: '0 4px 6px -1px rgba(25, 118, 210, 0.3)',
                }}
                onMouseEnter={(e) => {
                  if (!saving) e.currentTarget.style.backgroundColor = '#1565c0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1976d2';
                }}
              >
                {saving ? '💾 正在儲存數據...' : '💾 儲存所有數據'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px', border: '2px dashed #cbd5e1', borderRadius: '12px', color: '#64748b' }}>
            <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🏀</span>
            <h3 style={{ margin: '0 0 8px 0', fontWeight: '700' }}>尚未選擇比賽</h3>
            <p style={{ margin: 0, fontSize: '15px' }}>請先在上方下拉選單選擇賽事年度與場次，以載入球員名單並進行編輯。</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStats;
